/**
 * Slick Slider A11y Helper
 *
 * react-slick adds aria-hidden="true" on off-screen slides, but child
 * <a>, <button>, <input> elements remain Tab-focusable. This violates
 * WCAG 2.1 SC 4.1.2 (hidden content reachable by keyboard).
 *
 * This helper installs a single MutationObserver on document.body that
 * mirrors aria-hidden onto the modern `inert` attribute, which removes
 * descendants from sequential focus navigation in all evergreen browsers.
 *
 * Idempotent: calling install() multiple times is a no-op.
 */

let installed = false;

const SELECTOR = '.slick-slide';

function syncSlide(el) {
  if (!(el instanceof HTMLElement)) return;
  const hidden = el.getAttribute('aria-hidden') === 'true';
  if (hidden) {
    if (!el.hasAttribute('inert')) el.setAttribute('inert', '');
  } else if (el.hasAttribute('inert')) {
    el.removeAttribute('inert');
  }
}

function syncAll(root = document) {
  root.querySelectorAll(SELECTOR).forEach(syncSlide);
}

export function installSlickInert() {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  // Initial pass for already-rendered sliders
  syncAll();

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.attributeName === 'aria-hidden') {
        if (m.target instanceof HTMLElement && m.target.matches(SELECTOR)) {
          syncSlide(m.target);
        }
      } else if (m.type === 'childList') {
        m.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            if (node.matches?.(SELECTOR)) syncSlide(node);
            node.querySelectorAll?.(SELECTOR).forEach(syncSlide);
          }
        });
      }
    }
  });

  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-hidden'],
  });
}
